/**
 * @Copyright 2014-2017 DataVoke, Inc. All rights reserved.
 */

"use strict";

import {noop} from '../utils/tools';

// #region Local Vars

export const idb = (window != null
    ? window.indexedDB || window.mozIndexedDB || window.webkitIndexedDB || window.msIndexedDB || window.shimIndexedDB
    : global.indexedDB);

let idbCursor = {
    NEXT_NO_DUPLICATE: "nextunique"
};

let noOperationFunction = function () {};

export const dbs = {};

export const dataStores = {};

export var initialized = false;

// #endregion

// #region DB Definitions
export function initializeDBs () {
    dbs.entities = {
        name: app.dv.appSpace.token + "_Entities",
        version: 1
    };
    dbs.values = {
        name: app.dv.appSpace.token + "_Values",
        version: 1
    };
    dbs.transactions = {
        name: app.dv.appSpace.token + "_Transactions",
        version: 1
    };
    dbs.pendingChanges = {
        name: app.dv.appSpace.token + "_PendingChanges",
        version: 1
    };

    dataStores.RecordType = {
        name: "RecordType",
        keypath: "RecordTypeID",
        db: dbs.entities
    };
    dataStores.Category = {
        name: "Category",
        keypath: "CategoryID",
        db: dbs.entities
    };
    dataStores.Property = {
        name: "Property",
        keypath: "PropertyID",
        db: dbs.entities
    };
    dataStores.ValueStatus = {
        name: "ValueStatus",
        keypath: "ValueStatusID",
        db: dbs.entities
    };
    dataStores.View = {
        name: "View",
        keypath: "ViewID",
        db: dbs.entities
    };
    dataStores.Security = {
        name: "Security",
        keypath: "SecurityID",
        db: dbs.entities
    };
    dataStores.SecurityGroup = {
        name: "SecurityGroup",
        keypath: "SecurityGroupID",
        db: dbs.entities
    };
    dataStores.SecurityGroupLookup = {
        name: "SecurityGroupLookup",
        keypath: "SecurityGroupLookupID",
        db: dbs.entities
    };
    dataStores.AppContext = {
        name: "AppContext",
        keypath: "ContextID",
        db: dbs.entities
    };
    dataStores.Variable = {
        name: "Variable",
        keypath: "FilterString",
        db: dbs.entities
    };
    dataStores.Package = {
        name: "Package",
        keypath: "PackageID",
        db: dbs.entities
    };
    dataStores.Style = {
        name: "Style",
        keypath: "StyleID",
        db: dbs.entities
    };
    dataStores.Tag = {
        name: "Tag",
        keypath: "TagID",
        db: dbs.entities
    };
    dataStores.TagLink = {
        name: "TagLink",
        keypath: "TagLinkID",
        db: dbs.entities
    };
    dataStores.User = { // NOTE: this will likely not be used...AL
        name: "User",
        keypath: "UserID",
        db: dbs.entities
    };
    dataStores.PendingViewChanges = {
        name: "PendingViewChanges",
        keypath: "ViewID",
        db: dbs.pendingChanges
    };

    initialized = true;
}

// #endregion

// #region IndexedDB Functions

/**
 * Delete the specified database from local storage.
 * @param {object} db - the database object to delete
 * @param {function} [callback = noOperationFunction] - OPTIONAL callback function to return success or error.
 * @returns {Promise}
 */
export function deleteDB (db, callback = noOperationFunction) {
    return new Promise((resolve, reject) => {
        if (!initialized) {
            let notInitialized = new Error("Local storage has not been initialized.");
            return reject(notInitialized);
        }
        let dbreq = idb.deleteDatabase(db.name);
        dbreq.onsuccess = (event) => {
            //var db = event.result;
            callback(true);
            resolve(true);
        };
        dbreq.onerror = (event) => {
            callback(event);
            reject(event);
        };
    });
}

/**
 * Delete all local databases for this application
 * @param {Function} [callback = noop]
 * @returns {Promise<void>}
 */
export async function deleteAllDBs (callback = noop) {
    /**
     * Array for promises
     * @type {Array}
     */
    let promises = [];

    for (let db in dbs) {
        if (dbs.hasOwnProperty(db)) {
            // add the deletion of this DB to the promise array
            promises.push(deleteDB(dbs[db]));
        }
    }

    // wait for all the promises to finish
    await Promise.all(promises);

    callback();
}

/**
 * Open database specified by data store object.
 * @param {object} dataStore - Data store object to open.
 * @param {function} callback - Function to callback function with result or error.
 */
function openDB(dataStore, callback) {
    return new Promise((resolve, reject) => {
        if (!initialized) {
            let notInitialized = new Error("Local storage has not been initialized.");
            return reject(notInitialized);
        }
        let req;
        try {
            req = idb.open(dataStore.db.name, dataStore.db.version);
        }
        catch (e) {
            throw e;
        }
        req.onsuccess = (event) => {
            callback(event.target.result);
        };
        req.onerror = (event) => {
            callback(event);
        };
        req.onupgradeneeded = dbUpgradeNeeded;
    });
}

/**
 * Upgrade logic (invoked automatically when opening a DB and the requested version is different than the existing one -- or if no db yet exists).
 * @param {Event} event - The event resulting from an action that requires upgrading the database.
 */
function dbUpgradeNeeded(event) {
    if (!initialized) {
        let notInitialized = new Error("Local storage has not been initialized.");
        return reject(notInitialized);
    }

    let database = event.target.result;

    switch (database.name) {
        //switch (event.srcElement.transaction.db.name) {
        case dbs.entities.name:

            // Right now, this is treated as a new install of the db, so just build.  Later this will be extended to handle conversions when necessary.
            database.createObjectStore(dataStores.RecordType.name, { keyPath: dataStores.RecordType.keypath });
            database.createObjectStore(dataStores.Category.name, { keyPath: dataStores.Category.keypath });
            database.createObjectStore(dataStores.Property.name, { keyPath: dataStores.Property.keypath });
            database.createObjectStore(dataStores.ValueStatus.name, { keyPath: dataStores.ValueStatus.keypath });
            database.createObjectStore(dataStores.View.name, { keyPath: dataStores.View.keypath });
            database.createObjectStore(dataStores.Security.name, { keyPath: dataStores.Security.keypath });
            database.createObjectStore(dataStores.SecurityGroup.name, { keyPath: dataStores.SecurityGroup.keypath });
            database.createObjectStore(dataStores.SecurityGroupLookup.name, { keyPath: dataStores.SecurityGroupLookup.keypath });
            database.createObjectStore(dataStores.User.name, { keyPath: dataStores.User.keypath });
            database.createObjectStore(dataStores.Package.name, { keyPath: dataStores.Package.keypath });
            database.createObjectStore(dataStores.Style.name, { keyPath: dataStores.Style.keypath });
            database.createObjectStore(dataStores.Tag.name, { keyPath: dataStores.Tag.keypath });
            database.createObjectStore(dataStores.TagLink.name, { keyPath: dataStores.TagLink.keypath });
            database.createObjectStore(dataStores.Variable.name, { keyPath: dataStores.Variable.keypath });
            database.createObjectStore(dataStores.AppContext.name, { keyPath: dataStores.AppContext.keypath });
            break;

        case dbs.pendingChanges.name:
            database.createObjectStore(dataStores.PendingViewChanges.name, { keyPath: dataStores.PendingViewChanges.keypath });
            break;
    }

}

/**
 * Get a single record based off of type, and key
 * @param dataStore
 * @param key
 * @param {function} callback - Function to callback function with result or error.
 */
export function getRecord (dataStore, key, callback = noOperationFunction) {
    return new Promise((resolve, reject) => {
        if (!initialized) {
            let notInitialized = new Error("Local storage has not been initialized.");
            return reject(notInitialized);
        }
        openDB(dataStore, function (oDb) {
            let trans = oDb.transaction([dataStore.name]);
            let store = trans.objectStore(dataStore.name);
            let req = store.get(key);
            req.onsuccess = (event) => {
                callback(event.target.result, true);
                resolve(event.target.result);
            };
            req.onerror = (event) => {
                callback(event, false);
                reject(event);
            };
        });
    });
}

/**
 * Get all records that exist for type
 * @param {string} dataStore - Data store to pull records from.
 * @param {function} [callback = noOperationFunction] - OPTIONAL callback function for results or error.
 * @returns {Promise}
 */
export function getAllRecords (dataStore, callback = noOperationFunction) {
    return new Promise((resolve, reject) => {
        if (!initialized) {
            let notInitialized = new Error("Local storage has not been initialized.");
            return reject(notInitialized);
        }
        openDB(dataStore, function (oDb) {
            let trans = oDb.transaction([dataStore.name]);
            let store = trans.objectStore(dataStore.name);
            let req = store.openCursor();
            let records = [];
            req.onsuccess = (event) => {
                let cur = event.target.result;
                if (cur) {
                    records.push(cur.value);
                    cur.continue();
                }
                else {
                    // if the cursor is undefined, then the cursor is done itterating.
                    callback(records, true);
                    resolve(records);
                }
            };
            req.onerror = (event) => {
                callback(event, false);
                reject(event);
            };
        });
    });
}

/**
 *
 * @param {object} db
 * @param {string} storeName
 * @param {string} index
 * @param {function} callback - Function to callback function with result or error.
 * @returns {Promise}
 */
export function getDistinctRecords (db, storeName, index, callback = noOperationFunction) {
    return new Promise((resolve, reject) => {
        if (!initialized) {
            let notInitialized = new Error("Local storage has not been initialized.");
            return reject(notInitialized);
        }
        openDB(db, function (oDb) {
            let trans = oDb.transaction([storeName]);
            let store = trans.objectStore(storeName);
            let idx = store.index(index);
            let req = idx.openCursor(null, idbCursor.NEXT_NO_DUPLICATE);
            let records = [];
            req.onsuccess = (event) => {
                let cur = event.target.result;
                if (cur) {
                    records.push(cur.value);
                    cur.continue();
                }
                else {
                    // if the cursor is undefined, then the cursor is done itterating.
                    callback(records, true);
                    resolve(records);
                }
            };
            req.onerror = (event) => {
                callback(event, false);
                reject(event);
            };
        });
    });
}

/**
 *
 * @param {object} db
 * @param {string} storeName
 * @param {string} index
 * @param {string} wherePredicate
 * @param {function} callback - Function to callback function with result or error.
 * @returns {Promise}
 */
export function getRecordsDistinctWhere (db, storeName, index, wherePredicate, callback = noOperationFunction) {
    if (!initialized) {
        let notInitialized = new Error("Local storage has not been initialized.");
        return reject(notInitialized);
    }
    openDB(db, function (oDb) {
        let trans = oDb.transaction([storeName]);
        let store = trans.objectStore(storeName);
        let idx = store.index(index);
        let req = idx.openCursor(IDBKeyRange.only(wherePredicate), idbCursor.NEXT_NO_DUPLICATE);
        let records = [];
        req.onsuccess = (event) => {
            let cur = event.target.result;
            if (cur) {
                records.push(cur.value);
                cur.continue();
            }
            else {
                // if the cursor is undefined, then the cursor is done iterating.
                callback(records, true);
                resolve(records);
            }
        };
        req.onerror = (event) => {
            callback(event, false);
            reject(event);
        };
    });
}

/**
 *
 * @param {object} db
 * @param {string} storeName
 * @param {string} index
 * @param {string} wherePredicates
 * @param {function} callback - Function to callback function with result or error.
 * @returns {Promise}
 */
export function getRecordsWhereMuliPredicates (db, storeName, index, wherePredicates, callback = noOperationFunction) {
    return new Promise((resolve, reject) => {
        if (!initialized) {
            let notInitialized = new Error("Local storage has not been initialized.");
            return reject(notInitialized);
        }
        openDB(db, function (oDb) {
            let trans = oDb.transaction([storeName], "readonly");
            let store = trans.objectStore(storeName);
            let range = IDBKeyRange.only(wherePredicates);
            let idx = store.index(index);
            let req = idx.openCursor(range);
            let records = [];
            req.onsuccess = (event) => {
                let cur = event.target.result;
                if (cur) {
                    records.push(cur.value);
                    cur.continue();
                }
                else {
                    // if the cursor is undefined, then the cursor is done itterating.
                    callback(records);
                    resolve(records);
                }
            };
            req.onerror = (event) => {
                callback(event, false);
                reject(event);
            };
        });
    });
}

/**
 * Update or insert a single record
 * @param {object} dataStore - The object to store the record in.
 * @param {object} record - The object to store as a record identified by the index property defined by the data store.
 * @param {function} callback - Function to callback function with result or error.
 */
export function updateOrInsertRecord (dataStore, record, callback = noOperationFunction) {
    return new Promise((resolve, reject) => {
        if (!initialized) {
            let notInitialized = new Error("Local storage has not been initialized.");
            return reject(notInitialized);
        }
        openDB(dataStore, function (oDb) {
            let trans = oDb.transaction([dataStore.name], "readwrite");
            let store = trans.objectStore(dataStore.name);
            let req = store.put(record);
            req.onsuccess = (event) => {
                callback(event.target.result, true);
                resolve(event.target.result);
            };
            req.onerror = (event) => {
                callback(event, false);
                reject(event);
            };
        });
    });
}

/**
 * Add a new record to the specified data store.
 * @param {object} dataStore - The data store to add the record to.
 * @param {object} record - The object to add as a record; MUST have the "key" property defined by the data store.
 * @param {function} [callback = noOperationFunction] - OPTIONAL callback function for results or error.
 * @returns {Promise}
 */
export function addRecord (dataStore, record, callback = noOperationFunction) {
    return new Promise((resolve, reject) => {
        if (!initialized) {
            let notInitialized = new Error("Local storage has not been initialized.");
            return reject(notInitialized);
        }
        openDB(dataStore, function (oDb) {
            let trans = oDb.transaction([dataStore.name], "readwrite");
            let store = trans.objectStore(dataStore.name);
            let req = store.add(record); //, record.token);
            req.onsuccess = (event) => {
                callback(event.target.result, true);
                resovle(event.target.result);
            };
            req.onerror = (event) => {
                callback(event, false);
                reject(event);
            };
        });
    });
}

/**
 * Save the specified record object in the specified data store.
 * @param {object} dataStore
 * @param {object} record
 * @param {function} callback - Function to callback function with result or error.
 */
export function saveRecord (dataStore, record, callback = noOperationFunction) {
    return new Promise((resolve, reject) => {
        if (!initialized) {
            let notInitialized = new Error("Local storage has not been initialized.");
            return reject(notInitialized);
        }
        openDB(dataStore, function (oDb) {
            let trans = oDb.transaction([dataStore.name], "readwrite");
            let store = trans.objectStore(dataStore.name);
            let req = store.put(record); //, record.token);
            req.onsuccess = (event) => {
                callback(event.target.result, true);
                resolve(event.target.result);
            };
            req.onerror = (event) => {
                callback(event, false);
                reject(event);
            };
        });
    });
}

/**
 * Remove a record from the specified data store
 * @param {object} dataStore - The data store to delete a record from.
 * @param {string} key - The key value (property defined by the data store object) to use to identify the record to delete.
 * @param {function} [callback = noOperationFunction] - OPTIONAL callback function for results or error.
 * @returns {Promise}
 */
export function deleteRecord (dataStore, key, callback = noOperationFunction) {
    return new Promise((resolve, reject) => {
        if (!initialized) {
            let notInitialized = new Error("Local storage has not been initialized.");
            return reject(notInitialized);
        }
        openDB(dataStore, function (oDb) {
            let trans = oDb.transaction([dataStore.name], "readwrite");
            let store = trans.objectStore(dataStore.name);
            let req = store.delete(key);
            req.onsuccess = (event) => {
                callback(event, true);
                resolve(event);
            };
            req.onerror = (event) => {
                callback(event, false);
                reject(event);
            };
        });
    });
}

/**
 * Remove multiple records
 * @param dataStore
 * @param keys
 * @param {function} callback - Function to callback function with result or error.
 * @returns {Promise}
 */
export function deleteRecords (dataStore, keys, callback = noOperationFunction) {
    return new Promise((resolve, reject) => {
        if (!initialized) {
            let notInitialized = new Error("Local storage has not been initialized.");
            return reject(notInitialized);
        }
        openDB(dataStore, function (oDb) {
            let trans = oDb.transaction([dataStore.name], "readwrite");
            let store = trans.objectStore(dataStore.name);
            let i = 0;

            deleteNext();

            function deleteNext () {
                if (i < keys.length) {
                    if (keys[i] != null) {
                        store.delete(keys[i]).onsuccess = deleteNext;
                        ++i;
                    }
                    else {
                        ++i;
                        deleteNext();
                    }
                }
                else {
                    callback(null, true);
                    resolve();
                }
            }
        });
    });
}

/**
 * Remove all records...AL
 * @param dataStore
 * @param {function} callback - Function to callback function with result or error.
 * @returns {Promise}
 */
export function deleteAllRecords (dataStore, callback = noOperationFunction) {
    return new Promise((resolve, reject) => {
        if (!initialized) {
            let notInitialized = new Error("Local storage has not been initialized.");
            return reject(notInitialized);
        }
        openDB(dataStore, function (oDb) {
            let trans = oDb.transaction([dataStore.name], "readwrite");
            let store = trans.objectStore(dataStore.name);
            let req = store.clear();
            req.onsuccess = (event) => {
                callback(event, true);
                resolve(event);
            };
            req.onerror = (event) => {
                callback(event, false);
                reject(event);
            };
        });
    });
}

/**
 * Add multiple records
 * @param {object} dataStore
 * @param {object} records
 * @param {function} callback - Function to callback function with result or error.
 * @returns {Promise}
 */
export function updateOrInsertRecords (dataStore, records, callback = noOperationFunction) {
    return new Promise((resolve, reject) => {
        if (!initialized) {
            let notInitialized = new Error("Local storage has not been initialized.");
            return reject(notInitialized);
        }
        openDB(dataStore, function (oDb) {
            let trans = oDb.transaction([dataStore.name], "readwrite");
            let store = trans.objectStore(dataStore.name);
            let i = 0;

            putNext();

            function putNext () {
                if (i < records.length) {
                    store.put(records[i]).onsuccess = putNext;
                    ++i;
                }
                else {
                    callback(records, true);
                    resolve(records);
                }
            }
        });
    });
}

// #endregion
